import { MdFaceUnlock } from "react-icons/md";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  reopenApplication,
  fetchLeads,
  updateApplicationNoLoader,
} from "../../../../redux/features/adminLeadSlice";
import { fetchAgents } from "../../../../redux/features/agentSlice";

export default function ClientHistoryHeader({ client }) {
  const { user } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { agents } = useSelector((state) => state.agents || { agents: [] });

  const [filteredAgents, setFilteredAgents] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [applicationType, setApplicationType] = useState("");
  const [agent, setAgent] = useState("");
  const [reason, setReason] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false); // ✅ spinner state
  const [error, setError] = useState(""); // ✅ optional validation feedback

  useEffect(() => {
    if (!agents || agents.length === 0) {
      dispatch(fetchAgents());
    }
  }, [agents?.length, dispatch]);

  useEffect(() => {
    setFilteredAgents(
      Array.isArray(agents) ? agents.filter((a) => a?.isActive) : []
    );
  }, [agents]);

  const isFinalized =
    String(client?.status || client?.visaStatus) === "Finalized";
  const isAdminRoute = location.pathname.startsWith("/admin/");

  const handleViewLog = () => {
    const base = isAdminRoute
      ? "/admin/application-log"
      : "/agent/application-log";
    navigate(`${base}/${client._id}`);
  };

  const handleReopen = async () => {
    // ✅ Simple front-end validation
    if (!applicationType || !agent || !reason.trim()) {
      setError("All fields are required before reopening the lead.");
      return;
    }
    setError("");
    setIsSubmitting(true);

    try {
      const payload = {
        applicationId: client._id,
        agentId: agent,
        reason,
        applicationType,
        isFCC: applicationType.toLowerCase() === "fcc",
        isART: applicationType.toLowerCase() === "art",
      };

      const result = await dispatch(reopenApplication(payload));

      // ✅ Commented out - updateApplication API should not be triggered automatically after reopen
      // if (result.payload && result.payload.status) {
      //   await dispatch(
      //     updateApplicationNoLoader({
      //       id: client._id,
      //       formData: { visaStatus: result.payload.status },
      //     })
      //   );
      // }

      await dispatch(fetchLeads({}));
      setIsModalOpen(false);
      navigate(isAdminRoute ? "/admin/leads" : "/agent/leads");
    } catch (err) {
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isDisabled = !applicationType || !agent || !reason.trim() || isSubmitting;

  return (
    <div className="mb-6">
      {/* Client Info Card */}
      <div className="bg-white rounded-lg p-4 md:p-6 border shadow-custom">
        <div className="flex flex-col md:flex-row items-start md:justify-between gap-4">
          <div className="flex flex-col md:flex-row items-center gap-4 w-full">
            <div className="w-20 h-20 md:w-26 md:h-26 rounded-full border flex items-center justify-center">
              <MdFaceUnlock className="w-16 h-16 md:w-20 md:h-20 text-gray-800" />
            </div>
            <div className="text-center md:text-left">
              <div className="flex flex-col md:flex-row md:items-center gap-2">
                <h1 className="text-lg md:text-xl font-semibold">
                  {client.firstName + " " + client.lastName}
                </h1>
                <span className="px-3 py-1 bg-[#569A50] text-white rounded-md text-sm">
                  {client?.status === "WithdrawnByClient" || client?.status === "Cancelled"
                    ? "Cancelled"
                    : client?.status === "WithdrawnByAgent"
                      ? "Withdrawn"
                      : client?.status || "N/A"}
                </span>
              </div>
              <p className="text-gray-500 text-sm md:text-base">
                {client.uniqueId}
              </p>
              <p className="text-sm text-gray-600 mt-1">{client.visaCategory}</p>
            </div>
          </div>

          <div className="flex pt-1 flex-col md:flex-row gap-2 md:gap-3 w-full md:w-auto">
            <button
              onClick={handleViewLog}
              className="px-4 bg-white text-[#005787] border border-[#BFCAD2] rounded-md hover:bg-gray-50 transition-colors w-[230px]"
            >
              View Application Log
            </button>

            {isFinalized && (
              <button
                onClick={() => setIsModalOpen(true)}
                className="px-4 py-1 bg-[#005787] text-white rounded-md hover:bg-[#005887cb] transition-colors w-[180px]"
              >
                Reopen Lead
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setIsModalOpen(false)}
          />
          <div className="relative bg-white rounded-[12px] w-[80%] max-w-[480px] p-5 shadow-lg z-10">
            <h2 className="text-xl font-semibold mb-4 text-gray-900">
              Reopen Application
            </h2>

            <div className="space-y-5">
              <div>
                <label className="block text-[15px] text-gray-700 mb-1">
                  Select Application Type
                </label>
                <select
                  className="w-full border border-gray-300 rounded-md py-1 px-2.5 text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-400"
                  value={applicationType}
                  onChange={(e) => setApplicationType(e.target.value)}
                  required
                >
                  <option value="">Select Type</option>
                  <option value="FCC">FCC</option>
                  <option value="ART">ART</option>
                </select>
              </div>

              <div>
                <label className="block text-[15px] text-gray-700 mb-1">
                  Transfer to another Agent
                </label>
                <select
                  className="w-full border border-gray-300 rounded-md py-1 px-2.5 text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-400"
                  value={agent}
                  onChange={(e) => setAgent(e.target.value)}
                  required
                >
                  <option value="">Select Agent</option>
                  {filteredAgents?.map((a) => (
                    <option key={a._id} value={a._id}>
                      {a.firstName} {a.lastName}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-[15px] text-gray-700 mb-1">
                  Task Summary / Reason
                </label>
                <textarea
                  required
                  rows={4}
                  className="w-full border border-gray-300 rounded-md p-2.5 text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-400 resize-none"
                  placeholder="Enter reason or details here..."
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                />
              </div>
            </div>

            {error && (
              <p className="text-red-500 text-sm mt-2 text-center">{error}</p>
            )}

            <div className="mt-4 flex justify-between gap-4">
              <button
                className="flex-1 py-1 bg-[#C24B4B] text-white rounded-full hover:bg-[#a23c3c] transition"
                onClick={() => setIsModalOpen(false)}
              >
                Cancel
              </button>

              <button
                className={`flex-1 py-1 bg-[#0A558E] text-white rounded-full hover:bg-[#094976] transition flex justify-center items-center ${isDisabled ? "opacity-60 cursor-not-allowed" : ""
                  }`}
                onClick={handleReopen}
                disabled={isDisabled}
              >
                {isSubmitting ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  "Reopen"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
